import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import api, { resolveUrl } from '../lib/api';
import { useCart } from '../context/CartContext';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';
import ItemCustomizer from '../components/ItemCustomizer';

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('');
  const [q, setQ] = useState('');
  const [customizing, setCustomizing] = useState(null);
  const { add } = useCart();
  const { toast } = useToast();

  useEffect(() => {
    Promise.all([api.get('/menu'), api.get('/modifier-groups'), api.get('/categories')])
      .then(([m, mg, cat]) => {
        setItems(m.data.filter((i) => i.available));
        setModifierGroups(mg.data || []);
        setCategoriesData(cat.data || []);
        setOrderedCategories((cat.data || []).map((c) => c.name));
      })
      .finally(() => setLoading(false));
  }, []);

  const categoryImageByName = useMemo(() => {
    const map = {};
    categoriesData.forEach((c) => { if (c.image) map[c.name] = c.image; });
    return map;
  }, [categoriesData]);

  const searchFiltered = useMemo(() => {
    const query = q.toLowerCase().trim();
    if (!query) return items;
    return items.filter((i) => i.name.toLowerCase().includes(query) || (i.description || '').toLowerCase().includes(query));
  }, [items, q]);

  const grouped = useMemo(() => {
    const g = {};
    searchFiltered.forEach((i) => { g[i.category] = g[i.category] || []; g[i.category].push(i); });
    const keys = [...orderedCategories.filter((c) => g[c]), ...Object.keys(g).filter((c) => !orderedCategories.includes(c))];
    return keys.map((k) => [k, g[k]]);
  }, [searchFiltered, orderedCategories]);

  // Scrollspy: highlight the current section as user scrolls
  useEffect(() => {
    const onScroll = () => {
      const anchors = grouped.map(([cat]) => document.getElementById(`cat-${slug(cat)}`)).filter(Boolean);
      const y = window.scrollY + 200;
      for (let i = anchors.length - 1; i >= 0; i--) {
        if (anchors[i].offsetTop <= y) { setActive(anchors[i].dataset.cat); return; }
      }
      if (grouped[0]) setActive(grouped[0][0]);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [grouped]);

  const jump = (cat) => {
    const el = document.getElementById(`cat-${slug(cat)}`);
    if (el) window.scrollTo({ top: el.offsetTop - 140, behavior: 'smooth' });
  };

  const handleAdd = (item) => {
    if (item.modifierGroupIds?.length) setCustomizing(item);
    else { add({ id: item.id, name: item.name, price: item.price, image: item.image, pst: !!item.pst, pstAmount: item.pstAmount ?? null });
      toast({ title: 'Added to cart', description: item.name }); }
  };

  return (
    <main>
      <PageHeader title="Our Menu" breadcrumb="Menu" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Search */}
        <div className="mb-6 max-w-md">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search menu..."
                   className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 focus:outline-none" />
          </div>
        </div>

        {loading ? <div className="text-center text-gray-500 py-16">Loading menu...</div> :
         grouped.length === 0 ? <div className="text-center text-gray-500 py-16">No items match your search.</div> : (
          <div className="grid lg:grid-cols-[240px_1fr] gap-8">
            {/* Sticky vertical category rail — shows all categories at once, no scroll */}
            <aside className="hidden lg:block">
              <nav className="sticky top-28 bg-[#FDF3EC] rounded-3xl p-3 space-y-1">
                {grouped.map(([cat, list]) => (
                  <button key={cat} onClick={() => jump(cat)}
                          className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-2xl text-sm font-semibold transition ${active === cat ? 'bg-[#ED2E2E] text-white shadow-md' : 'text-[#0F1C2E] hover:bg-white'}`}>
                    {categoryImageByName[cat] ? (
                      <img src={resolveUrl(categoryImageByName[cat])} alt="" className={`w-7 h-7 rounded-lg object-cover shrink-0 ${active === cat ? 'ring-2 ring-white' : ''}`} />
                    ) : (
                      <span className={`w-7 h-7 rounded-lg shrink-0 ${active === cat ? 'bg-white/25' : 'bg-white'}`} />
                    )}
                    <span className="truncate flex-1">{cat}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${active === cat ? 'bg-white/25 text-white' : 'bg-white text-gray-500'}`}>{list.length}</span>
                  </button>
                ))}
              </nav>
            </aside>

            {/* Item sections */}
            <div className="space-y-14">
              {/* Mobile category chips */}
              <div className="lg:hidden -mx-6 px-6 overflow-x-auto flex gap-2 sticky top-20 bg-white z-20 py-3 border-b border-gray-100">
                {grouped.map(([cat]) => (
                  <button key={cat} onClick={() => jump(cat)}
                          className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition ${active === cat ? 'bg-[#ED2E2E] text-white' : 'bg-[#FDF3EC] text-[#0F1C2E]'}`}>
                    {cat}
                  </button>
                ))}
              </div>

              {grouped.map(([cat, list]) => (
                <section key={cat} id={`cat-${slug(cat)}`} data-cat={cat} className="scroll-mt-32">
                  <div className="flex items-center gap-4 mb-6">
                    {categoryImageByName[cat] && (
                      <img src={resolveUrl(categoryImageByName[cat])} alt={cat} className="w-14 h-14 md:w-16 md:h-16 rounded-2xl object-cover shadow-sm ring-2 ring-[#FDF3EC]" />
                    )}
                    <h2 className="section-title text-2xl md:text-4xl">{cat}</h2>
                  </div>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {list.map((i) => (
                      <div key={i.id} className="group bg-white rounded-3xl overflow-hidden ring-1 ring-gray-100 hover:ring-[#ED2E2E]/30 hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col">
                        <div className="aspect-[4/3] bg-[#FDF3EC] overflow-hidden">
                          {i.image ? (
                            <img src={resolveUrl(i.image)} alt={i.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#ED2E2E] font-display font-bold text-3xl">WRAPPUS</div>
                          )}
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="font-heading font-bold text-[#0F1C2E] text-lg leading-snug">{i.name}</h3>
                            <span className="text-[#ED2E2E] font-bold text-lg whitespace-nowrap">${i.price.toFixed(2)}</span>
                          </div>
                          {i.description && <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">{i.description}</p>}
                          <button onClick={() => handleAdd(i)}
                                  className="btn-wrappus text-sm w-full justify-center mt-auto">
                            <Plus size={16} />
                            {i.modifierGroupIds?.length ? 'Customize & Add' : 'Add to Cart'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}
      </div>

      {customizing && (
        <ItemCustomizer
          item={customizing} modifierGroups={modifierGroups}
          onClose={() => setCustomizing(null)}
          onAdd={(customized) => { add(customized); toast({ title: 'Added to cart', description: customized.name }); }}
        />
      )}
    </main>
  );
}

const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
